import { Events } from '../events/app-events';
import { eventBus } from '../utils/event-bus';
import { undefFilter } from '@ahoy/model/dist/utils/undef-filter';
import { createAndViewConditionController, createViewConditionController } from './conditionals/viewConditions';
import { createQuestionFromRaw, Question } from './question';
import { Section } from '@ahoy/model/dist/types';
import type { QuestionnaireSchema } from '@ahoy/types/questionnaire-schema';
import { MobileDeviceService } from '@APP/services/mobile-device';
import { ServerQuestionsModel } from '@ahoy/model/dist/questions';

type Progress = {
  done: number;
  total: number;
};

export class QuestionsModel extends ServerQuestionsModel {
  private sectionsGroups: string[][] = [[]];
  private currentSection: Section | undefined;
  private lastSectionIdx: number = 0;
  private leftSectionIdx: number = 0;
  private lastSectionQuestion: Question | undefined;
  private leftSectionQuestion: Question | undefined;
  private lastErrorQuestion: Question | undefined;
  private serverErrorQuestion: Question | undefined;
  private uwReason: Question | undefined;
  private handlerSubscriptions: Function[] = [];
  private progress: Record<string, Progress> = {};
  private previusValues: Map<string, any> = new Map<string, any>();
  private boatQuestions = [
    'boatType',
    'length',
    'hullType',
    'motorType',
    'numberOfEngine',
    'horsepower',
    'dieselFuel',
    'enginePackage',
    'hasTrailer',
    'value',
    'overrideValue',
  ];

  private nkllQuestions = [
    'insurance_start_date',
    'nklSignature',
    'nklCustomerName',
    'nklDate',
  ];

  init(rawData: QuestionnaireSchema, overrides: any = {}) {

    const lockAll = rawData.questionnaireStatus === 'Completed';
    const isMobile = MobileDeviceService.mobileDetected;

    const rawQuestions = rawData.questions || [];
    const questions = undefFilter(
      rawQuestions.map((rawQuestion) => {
        const question = createQuestionFromRaw(rawQuestion, overrides, rawData);

        if (question) {
          question.locked = (question.locked ?? false) || lockAll;
        }
        if (question && rawQuestion.viewCondition) {
          const conditions = rawQuestion.viewCondition.split(' && ');
          if (conditions.length > 1) {
            const unsubs = createAndViewConditionController(question, conditions);
            this.handlerSubscriptions.push(...unsubs);
          } else if (conditions.length === 1) {
            const unsub = createViewConditionController( question, conditions[0] );
            if (unsub) {
              this.handlerSubscriptions.push(unsub);
            }
          }
        }
        return question;
      }),
    );

    super.init(rawData, isMobile, overrides, questions);

    this.sectionsGroups = rawData.sections;

    this.getQuestions().forEach((q) => { eventBus().emit( Events.questionIntermediateChangeEvent(q.questionId), q.value );
    });

    this.calculateProgress();
    this.lastSectionQuestion = this.getQuestionById('last_section');
    this.lastSectionIdx = this.getSections().findIndex((s) => s.sectionId === this.lastSectionQuestion?.value);
    this.leftSectionQuestion = this.getQuestionById('left_section');
    this.lastErrorQuestion = this.getQuestionById('last_error');
    this.serverErrorQuestion = this.getQuestionById('Server_Error');
    this.uwReason = this.getQuestionById('Underwriting_restriction_reason');

    if (!this.currentSection && this.getSections().length) {
      if (lockAll) {
        this.setActiveSection('S_Summary');
      }
      else {
        this.setActiveSection(this.getSections()[0].sectionId);
      }
    }

  }

  calculateProgressInSection(section?: Section) {
    if (section) {
      const { sectionId } = section;
      const progress: Partial<Progress> = this.progress[sectionId] || {};
      if (!this.getSectionById(sectionId)) {
        return;
      }
      const questions = undefFilter(this.getQuestionsBySection(sectionId).map(
        (q: any) => this.getQuestionById(q?.questionId as string),
      ));

      const total = questions.filter((question) => !question.hide && !(question.skippable && question.isEmpty())).length;

      const done = questions.filter((question) => question.isDone()).length;

      if (progress.total !== total || progress.done !== done) {
        this.progress[sectionId] = {
          total,
          done,
        };
        eventBus().emit(Events.Type.ProgressChanged, {
          section: section.sectionId,
          progress,
        });
      }
    }
  }

  lockPhone(lockEmail: boolean) {
    this.getQuestionById('owner_phone')!.locked = true;
    if (lockEmail) {
      this.getQuestionById('email')!.locked = true;
    }
  }

  calculateProgress() {
    this.getSections().forEach((section) =>
      this.calculateProgressInSection(section),
    );
  }



  getLastSection(): string {
    if (!this.getQuestionnaireId()) return '';
    const ABtestingQuestion = this.getQuestionById('AB_Testing');
    const ABtestingQuestionAsJson = ABtestingQuestion?.value && typeof ABtestingQuestion?.value === 'string'&& JSON.parse(ABtestingQuestion?.value);
    const isOcr = ABtestingQuestionAsJson?.ocr === 'with_form';
    const isMobile = MobileDeviceService.mobileDetected;
    let lastSection = this.lastSectionQuestion?.value as string || (isOcr && !isMobile? 'S_Boat_OCR' :'S_Boat');
    return lastSection;
  }


  getLeftSection(): string {
    if (!this.getQuestionnaireId()) return '';
    let leftSection = this.leftSectionQuestion?.value as string || this.getLastSection();

    /** Returning to S_Payment if last section is S_Payment_ACH_Summary */
    const isRenewal = this.getQuestionById('Is_Renewal')?.value === '$_BOOL_true';
    if (isRenewal) {
      if (['S_Payment_ACH_Summary', 'S_Payment'].includes(leftSection)) leftSection = 'S_Billing';      
    } else {    
      if (leftSection === 'S_Payment_ACH_Summary') leftSection = 'S_Payment';
    }
    return leftSection;
  }


  public setLastSection(sectionId: string | undefined) {
    if (!this.getQuestionnaireId()) return;
    this.lastSectionQuestion!.value = sectionId || 'S_Boat';
    this.lastErrorQuestion!.value = '';
    this.uwReason!.value = '';
    this.lastSectionIdx = this.getSections().findIndex((s) => s.sectionId === this.lastSectionQuestion!.value);
  }

  setLeftSection(sectionId: string | undefined) {
    const leftSectionQuestion = this.leftSectionQuestion;
    if (!this.getQuestionnaireId() || !leftSectionQuestion) return;
    leftSectionQuestion.value = sectionId || 'S_Boat';
    this.leftSectionIdx = this.getSections().findIndex((s) => s.sectionId === leftSectionQuestion.value);

  }


  public setLastError(error: string) {
    if (!this.getQuestionnaireId()) return;
    if (this.lastErrorQuestion!.value !== error) {
      this.uwReason!.value = '';
    }
    this.lastErrorQuestion!.value = error;
    if (!error) {
      this.serverErrorQuestion!.value = '';
    }
  }

  setActiveSection(sectionId: string) {
    const idx = this.getSections().findIndex((s) => s.sectionId === sectionId);
    if (idx >= 0) {
      const targetSection = this.getSections()[idx];
      if (this.getQuestionnaireId()) {
        if (idx > this.lastSectionIdx) {
          this.lastSectionIdx = idx;
          this.lastSectionQuestion!.value = sectionId;
        }
        if (idx > this.leftSectionIdx) {
          this.leftSectionQuestion!.value = sectionId;
          this.leftSectionIdx = idx;
        }
      }
      if (targetSection !== this.currentSection) {
        this.calculateProgressInSection(this.currentSection);
        this.calculateProgressInSection(targetSection);
        this.currentSection = this.getSections()[idx];
        eventBus().emit(Events.Type.SectionChanged, { sectionId: sectionId });
      }
    }
  }

  getSectionsGroup() {
    const active = this.getActiveSectionName();
    const group = this.sectionsGroups.map((s, i) => s.map(s => ({ ind: i, section: s })))?.flat().find(s => s.section == active)?.ind || 0;
    const sections = this.sectionsGroups.map((s, i) => s.map(s => ({ ind: i, section: s }))).flat().filter(s => s.ind == group).map(s => s.section);

    return sections.map((section) => ({
      name: section,
      active: section === active,
    }));
  }

  setActiveSectionOnBackButton(sectionId: string) {
    const idx = this.getSections().findIndex((s) => s.sectionId === sectionId);
    if (idx >= 0) {
      const targetSection = this.getSections()[idx];
      if (this.getQuestionnaireId()) {
        if (idx > this.lastSectionIdx) {
          this.lastSectionIdx = idx;
          this.lastSectionQuestion!.value = sectionId;
        }
        if (idx > this.leftSectionIdx) {
          this.leftSectionQuestion!.value = sectionId;
          this.leftSectionIdx = idx;
        }
      }
      if (targetSection !== this.currentSection) {
        this.calculateProgressInSection(this.currentSection);
        this.calculateProgressInSection(targetSection);
        this.currentSection = this.getSections()[idx];
        eventBus().emit(Events.Type.SectionChanged, { goingBack: true, sectionId: sectionId });
      }
    }
  }

  getActiveSectionName() {
    return this.currentSection?.sectionId;
  }

  getSectionBefore(
    sectionId: string = this.getActiveSectionName() || '',
  ): undefined | string {
    if (!sectionId) return undefined;

    const customerType = this.getQuestionById('Application_Type')?.value;
    if (customerType === 'Type2') {
      if (sectionId === 'S_Pricing') {
        return 'S_Owner';
      }
      if (sectionId === 'S_Feedback') {
        return 'S_Pricing';
      }
    }

    const idx = this.getSections().findIndex((s) => s.sectionId === sectionId);
    return this.getSections()[idx - 1]?.sectionId;
  }

  getSectionAfter(
    sectionId: string = this.getActiveSectionName() || '',
  ): undefined | string {
    if (!sectionId) return undefined;
    const idx = this.getSections().findIndex((s) => s.sectionId === sectionId);

    const customerType = this.getQuestionById('Application_Type')?.value;
    if (sectionId === 'S_Owner' && customerType === 'Type2') {
      return 'S_Pricing';
    }
    if (sectionId === 'S_Pricing' && customerType === 'Type2') {
      return 'S_Feedback';
    }
    return this.getSections()[idx + 1]?.sectionId;
  }


  isSectionAfterLeftSection(sectionId: string) {
    const idx = this.getSections().findIndex((s) => s.sectionId === sectionId);
    return idx > this.leftSectionIdx;
  }

  getProgress() {
    return this.progress;
  }

  getSectionProgress(sectionId: string) {
    return this.progress[sectionId];
  }

  getBlockToStart(sectionId: string, goingBack: boolean) {
    if (!this.getQuestionnaireId()) {
      return 0;
    }
    if (goingBack) {
      return (this.getBlocksBySection(sectionId)?.length || 1) - 1;
    }
    return 0;
  }


  getCurrentSectionLayout(): string {
    const currentSection = this.currentSection?.sectionId || '';
    const layoutGrouping = this.getLayoutGroupingInternal();
    if (!layoutGrouping || !layoutGrouping[currentSection]) return '';
    return layoutGrouping[currentSection][0][0].layout || '';
  }


  changeToMemoryMode() {
    this.getQuestions().forEach(question => {
      this.previusValues.set(question.questionId, question.value);
    });
  }

  saveMemoryMode() {
    let values: Map<string, any> = new Map<string, any>();
    this.boatQuestions.forEach(q => {
      values.set(q, this.getQuestionById(q)?.value);
    });

    this.boatQuestions.forEach(q => {
      this.getQuestionById(q)!.value = this.previusValues.get(q);
      this.getQuestionById(q)!.value = values.get(q);
    });
    this.nkllQuestions.forEach(q => {
      values.set(q, this.getQuestionById(q)?.value);
    });

    this.nkllQuestions.forEach(q => {
      this.getQuestionById(q)!.value = this.previusValues.get(q);
      this.getQuestionById(q)!.value = values.get(q);
    });

    this.previusValues = new Map<string, any>();
  }

  cancelMemoryMode() {
    this.getQuestions().forEach(q => {
      const value = this.previusValues.get(q.questionId);
      this.getQuestionById(q.questionId)!.value = value;
    });
    this.previusValues = new Map<string, any>();
  }

}
